/**
 * Purpose:
 *   Define the pure, shared URL contract for authenticated account routes and tabs.
 *
 * Notes:
 *   This module has no React or feature UI dependencies so pages, permissions, and hooks
 *   can resolve the same account route semantics.
 */

export type AccountTabRoute =
  | 'accounts'
  | 'users'
  | 'addresses'
  | 'payment_methods'
  | 'payment_records'
  | 'service_accounts';

export const accountTabToSlug: Record<AccountTabRoute, string> = {
  accounts: 'accounts',
  users: 'users',
  addresses: 'addresses',
  payment_methods: 'payment-methods',
  payment_records: 'payment-records',
  service_accounts: 'service-accounts',
};

const slugToAccountTab = Object.entries(accountTabToSlug).reduce<
  Record<string, AccountTabRoute>
>((tabs, [tab, slug]) => {
  tabs[slug] = tab as AccountTabRoute;
  return tabs;
}, {});

export const ACCOUNT_DEFAULT_TAB: AccountTabRoute = 'users';

const ACCOUNT_OVERVIEW_ROOT = '/account/overview';
const ACCOUNT_ID_SEGMENT_PATTERN = /^acc_[A-Za-z0-9_-]+$/;

function isAccountTabSlug(value: string): boolean {
  return value in slugToAccountTab;
}

function isValidAccountDetailPath(segments: string[]): boolean {
  if (segments.length === 0) return true;
  if (segments.length === 1) return segments[0] === 'new' || segments[0] === 'edit';

  const [resource, resourceId, action] = segments;
  if (resource !== 'users' && resource !== 'addresses') return false;
  if (segments.length === 2) return resourceId === 'new';

  return segments.length === 3 && Boolean(resourceId) && action === 'edit';
}

function isValidServiceAccountPath(segments: string[]): boolean {
  const [resource, id, action] = segments;
  if (resource !== accountTabToSlug.service_accounts || !id) return false;
  if (segments.length === 2) return true;
  return segments.length === 3 && id !== 'new' && action === 'edit';
}

/** Returns the account root when a page URL is outside the supported account route grammar. */
export function resolveInvalidAccountPathRedirect(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'account') return null;

  const [, scope, ...remainder] = segments;
  if (!scope) return null;
  if (isAccountTabSlug(scope)) return remainder.length === 0 ? null : ACCOUNT_OVERVIEW_ROOT;

  if (scope === 'overview') {
    const [accountId, ...detailSegments] = remainder;
    if (!accountId) return null;
    if (ACCOUNT_ID_SEGMENT_PATTERN.test(accountId)) {
      return isValidAccountDetailPath(detailSegments) ? null : ACCOUNT_OVERVIEW_ROOT;
    }
    return isValidAccountDetailPath(remainder) || isValidServiceAccountPath(remainder)
      ? null
      : ACCOUNT_OVERVIEW_ROOT;
  }

  if (ACCOUNT_ID_SEGMENT_PATTERN.test(scope)) {
    return remainder.length <= 1 &&
      (!remainder[0] || isAccountTabSlug(remainder[0])) &&
      remainder[0] !== accountTabToSlug.service_accounts
      ? null
      : ACCOUNT_OVERVIEW_ROOT;
  }

  return ACCOUNT_OVERVIEW_ROOT;
}

export function isAccountTabValue(value: string): value is AccountTabRoute {
  return value in accountTabToSlug;
}

export function parseAccountTabFromSlug(slug?: string): AccountTabRoute {
  if (!slug) return ACCOUNT_DEFAULT_TAB;
  if (isAccountTabValue(slug)) return slug;
  return slugToAccountTab[slug] ?? ACCOUNT_DEFAULT_TAB;
}

export function isAccountOverviewPath(pathname: string): boolean {
  return /^\/account\/overview(?:\/[^/]+)?\/?$/.test(pathname);
}

export function parseAccountTabFromPath(
  pathname: string,
  search = ''
): AccountTabRoute | undefined {
  const pathTab = pathname.match(
    /^\/account(?:\/[^/]+)?\/(users|accounts|addresses|payment-methods|payment-records|service-accounts)\/?$/
  )?.[1];

  if (pathTab) return parseAccountTabFromSlug(pathTab);
  if (isAccountOverviewPath(pathname)) {
    const legacyTab = new URLSearchParams(search).get('tab');
    return legacyTab ? parseAccountTabFromSlug(legacyTab) : undefined;
  }

  return undefined;
}

export function buildAccountTabHref(
  accountId: string | undefined,
  tab: AccountTabRoute
): string {
  const basePath = accountId ? `/account/${accountId}` : '/account';
  return `${basePath}/${accountTabToSlug[tab]}`;
}
